<template>
	<div
		class="relative flex items-center w-full rem:p-[1px] transition-all duration-300"
		:class="[
			isShoppingListOpen && !productDetail ? 'bg-white' : 'shadow-xs bg-background-secondary',
			buttonStyle ? '!bg-transparent !shadow-none gap-1 lg:gap-4' : '',
		]"
	>
		<ProductShoppingListPickerPanel
			:visible="!!isShoppingListOpen"
			@close="isShoppingListOpen = false"
		/>
		<div class="inline-flex grow">
			<div
				v-if="buttonStyle"
				class="inline-grid grow"
			>
				<button
					type="button"
					class="col-start-1 row-start-1 btn btn-primary btn-sm sm:btn-lg w-full sm:px-4 lg:px-10 justify-center whitespace-nowrap focus-outline disabled:opacity-60 grow"
					:class="[
						showStepper ? 'invisible pointer-events-none' : '',
						isTextItem && isOnList ? '!bg-background-noticeable !text-body-primary !border-border-secondary' : '',
					]"
					:disabled="isLocked"
					:tabindex="showStepper ? -1 : undefined"
					:aria-hidden="showStepper"
					:aria-label="isTextItem && isOnList ? 'Odebrat ze seznamu' : 'Přidat na seznam'"
					@click="onIconClick"
				>
					<span class="inline-flex items-center justify-center gap-2">
						<NuxtIcon
							name="custom/shopping-list-add"
							class="text-body-primary text-xl"
						/>
						{{ isTextItem && isOnList ? 'Odebrat ze seznamu' : 'Přidat na seznam' }}
					</span>
				</button>
				<div
					v-show="showStepper"
					class="col-start-1 row-start-1 w-full min-w-0 self-center"
				>
					<PlusMinus
						#default="{value}"
						:model-value="stepperAmount"
						:max="99"
						:disabled="isLocked"
						removable
						remove-icon-class="[&_svg]:!text-icon-tertiary"
						full-width
						class="w-full"
						:label-left="stepperAmount <= 1 ? 'Odstranit ze seznamu' : 'Odebrat'"
						label-right="Přidat"
						@update:model-value="onUpdate"
						@remove="onRemove"
					>
						<span class="text-center w-full">{{ value }}</span>
					</PlusMinus>
				</div>
			</div>
			<Transition
				v-else
				mode="out-in"
				enter-active-class="transition duration-300 origin-center"
				leave-active-class="transition duration-150 origin-center [&_[data-count]]:opacity-0"
				enter-from-class="opacity-0 scale-x-75"
				leave-to-class="opacity-0 scale-x-75"
			>
				<div
					v-if="showStepper"
					key="stepper"
				>
					<PlusMinus
						#default="{value}"
						:model-value="stepperAmount"
						:max="99"
						:disabled="isLocked"
						removable
						:label-left="stepperAmount <= 1 ? 'Odstranit ze seznamu' : 'Odebrat'"
						label-right="Přidat"
						@update:model-value="onUpdate"
						@remove="onRemove"
					>
						<span class="text-center">{{ value }}</span>
					</PlusMinus>
				</div>
				<button
					v-else
					key="icon"
					type="button"
					class="focus-outline disabled:opacity-60 h-11 flex items-center justify-center gap-1.5 rounded-full border border-border-secondary shadow-green-sm transition-all duration-150 hover:bg-background-noticeable"
					:class="isOnList ? 'px-2.5 bg-background-noticeable' : 'w-11 bg-white'"
					:disabled="isLocked"
					:aria-label="isOnList ? 'Odebrat ze seznamu' : 'Přidat na seznam'"
					@click="onIconClick"
				>
					<span v-if="isOnList" data-count class="typo-body-m text-body-primary">
						{{ amount }}
					</span>
					<NuxtIcon
						name="custom/shopping-list-add"
						class="text-icon-primary text-xl"
					/>
				</button>
			</Transition>
		</div>
		<button
			v-if="isAuthenticated"
			type="button"
			data-shopping-list-toggle
			class="w-8 sm:w-11 h-8 sm:h-11 flex-none flex items-center justify-center rounded-full transition-colors duration-300 focus-outline hover:bg-background-noticeable"
			:class="{'bg-background-noticeable': isShoppingListOpen}"
			aria-label="Zobrazit možnosti"
			@click="handleMoreButtonClick"
		>
			<NuxtIcon
				name="more-vertical"
				class="text-icon-primary text-xl transition-transform duration-300"
				:class="isShoppingListOpen ? 'rotate-90' : 'rotate-0'"
			/>
		</button>
	</div>
</template>

<script setup lang="ts">
	import type {Gsoa_ProductCatalog_ProductDetail as ProductDetail} from '~/openapi';
	import type {ShoppingListAddMethod} from '~/utils/tracking';

	interface Props {
		product?: ProductDetail;
		productDetail?: boolean;
		buttonStyle?: boolean;
		disabled?: boolean;
		/** GTM `shopping_list_add_item` method (source of the add interaction). */
		method?: ShoppingListAddMethod;
	}

	const props = withDefaults(defineProps<Props>(), {disabled: false, method: 'product_detail'});
	const isShoppingListOpen = defineModel<boolean>('isShoppingListOpen');

	const {isAuthenticated} = useAuth();

	const isExpanded = ref(false);

	const {
		amount,
		add,
		isTextItem,
		stepperAmount,
		isLocked,
		onUpdate,
		onRemove: removeFromList,
	} = useProductShoppingListStepper({
		product: () => props.product,
		disabled: () => props.disabled,
		method: props.method,
		onListChange: (value) => {
			isExpanded.value = value > 0;
		},
	});

	const isOnList = useHydrationSafeListMembership(amount);
	const showStepper = computed(() => !isTextItem.value && (isOnList.value || isExpanded.value));

	async function onIconClick() {
		if (isLocked.value) {
			return;
		}
		if (isTextItem.value) {
			if (isOnList.value) {
				await onRemove();
			} else {
				await add();
			}
			return;
		}
		if (amount.value === 0) {
			await add();
		}
		isExpanded.value = true;
	}

	async function onRemove() {
		await removeFromList(() => {
			isExpanded.value = false;
		});
	}

	function handleMoreButtonClick() {
		isShoppingListOpen.value = !isShoppingListOpen.value;
	}

	watch(amount, (value) => {
		if (value === 0) {
			isExpanded.value = false;
		}
	});
</script>
